/**
 * prepRollup.ts
 * Pure model for prep DISPLAY consolidation (BUG-2026-0165, built 2026-07-26).
 *
 * The scenario planner generates prep (Cutting / Grinding / Conformity) PER
 * TEST LINE on purpose — per-test opt-outs (RockPrepStepExclusion), sticky
 * removals (PrepUserRemoved), and machine occupancy all hang off individual
 * ScheduleTask rows. But the lab READS prep as one activity per step
 * ("cutting is cutting" — Adin, Jul-23 meeting), so the UI folds the rows
 * into one aggregate line per prep step. This module is that fold:
 * data-only, no JSX, unit-tested — and deliberately shaped so the upcoming
 * people-hours demand model can consume the same {taskType, qty, minutes}
 * parts instead of re-deriving them.
 *
 * Grouping key = the prep row's TaskTypeKey (falling back to Name), within
 * one source's task list. Groups with a single member are left as plain rows.
 */

import type { TaskWithBookings } from '../../../../services/scheduleTasks';

/** One member of a prep group, labeled by the TEST it preps. */
export interface PrepRollupPart {
  taskKey: string;
  /** The parent test's display name (non-prep sibling on the same source line), or null. */
  parentName: string | null;
  /** Specimen count being prepped (the line's TestQty), or null. */
  qty: number | null;
  /** Qty-scaled TOTAL minutes stored on the prep row (see taskRowMath), or null. */
  totalMinutes: number | null;
  /** Back-derived per-test minutes (totalMinutes ÷ qty), or null. */
  perTestMinutes: number | null;
}

export interface PrepGroup {
  /** Stable key for expand-state maps: `prep:<TaskTypeKey|Name>`. */
  groupKey: string;
  /** The prep step's display name (e.g. "Cutting (Preparation)"). */
  name: string;
  taskTypeKey: string | null;
  members: TaskWithBookings[];
  parts: PrepRollupPart[];
  /** Σ member total minutes (null when NO member has minutes). */
  totalMinutes: number | null;
  /** Σ member TestQty (null when NO member has a qty). */
  totalTests: number | null;
  /** Aggregate span across members' bookings. */
  startDate: string | null;
  endDate: string | null;
  /** Deduped machine labels across members (code or name). */
  machineLabels: string[];
}

export type TaskListEntry =
  | { kind: 'task'; task: TaskWithBookings }
  | { kind: 'prepGroup'; group: PrepGroup };

/** The source line key a task hangs off (quote line or WO detail). */
function lineKey(t: TaskWithBookings): string | null {
  return t.QuoteLineItemKey ?? t.WorkOrderDetailKey ?? null;
}

/**
 * Fold a source's task list into render entries, order preserved: non-prep
 * tasks stay 1:1; prep rows of the same step collapse into ONE group entry at
 * the position of their first member. Single-member "groups" stay plain rows.
 */
export function rollupPrepTasks(tasks: readonly TaskWithBookings[]): TaskListEntry[] {
  // Parent-test lookup: source line key → the non-prep task's name.
  const parentByLine = new Map<string, string>();
  for (const t of tasks) {
    const lk = lineKey(t);
    if (!t.IsPrepStep && lk) parentByLine.set(lk, t.Name);
  }

  const groups = new Map<string, PrepGroup>();
  const order: TaskListEntry[] = [];

  for (const t of tasks) {
    if (!t.IsPrepStep) {
      order.push({ kind: 'task', task: t });
      continue;
    }
    const key = `prep:${t.TaskTypeKey ?? t.Name}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        groupKey: key,
        name: t.Name,
        taskTypeKey: t.TaskTypeKey,
        members: [],
        parts: [],
        totalMinutes: null,
        totalTests: null,
        startDate: null,
        endDate: null,
        machineLabels: [],
      };
      groups.set(key, g);
      order.push({ kind: 'prepGroup', group: g });
    }
    g.members.push(t);
    const lk = lineKey(t);
    const qty = t.TestQty ?? null;
    const totalMinutes = t.MachineMinutes ?? null;
    g.parts.push({
      taskKey: t.ScheduleTaskKey,
      parentName: lk ? (parentByLine.get(lk) ?? null) : null,
      qty,
      totalMinutes,
      perTestMinutes: totalMinutes != null && qty ? Math.round((totalMinutes / qty) * 10) / 10 : null,
    });
    if (totalMinutes != null) g.totalMinutes = (g.totalMinutes ?? 0) + totalMinutes;
    if (qty != null) g.totalTests = (g.totalTests ?? 0) + qty;
    if (t.StartDate && (!g.startDate || t.StartDate < g.startDate)) g.startDate = t.StartDate;
    if (t.EndDate && (!g.endDate || t.EndDate > g.endDate)) g.endDate = t.EndDate;
    for (const b of t.Bookings) {
      const label = b.MachineCode ?? b.MachineName;
      if (label && !g.machineLabels.includes(label)) g.machineLabels.push(label);
    }
  }

  // Demote single-member groups back to plain rows (nothing to consolidate).
  return order.map((e) =>
    e.kind === 'prepGroup' && e.group.members.length < 2
      ? ({ kind: 'task', task: e.group.members[0] } as TaskListEntry)
      : e,
  );
}

/** A prep group's members that have NO crew, labelled by the test they prep. */
export interface PrepStaffingGap {
  /** The unstaffed member tasks (subset of `group.members`, order preserved). */
  members: TaskWithBookings[];
  /** One label per unstaffed member: the parent test's name, else the prep row's own name. */
  labels: string[];
}

/**
 * Which members of a consolidated prep group still have nobody on them
 * (BUG-2026-0203). A prep group's row shows the UNION of its members' crew, so
 * a step that is staffed on one test line but not another (typical after a
 * work-order re-sync adds a line) would otherwise read as fully staffed while
 * the header's per-task count says "1 unstaffed". The table uses this to show
 * a "N of M unstaffed" cue and to aim the assign picker at just those members.
 */
export function unstaffedPrepMembers(
  group: PrepGroup,
  assignmentMap: Record<string, readonly unknown[]>,
): PrepStaffingGap {
  const members = group.members.filter((m) => (assignmentMap[m.ScheduleTaskKey] ?? []).length === 0);
  const labels = members.map(
    (m) => group.parts.find((p) => p.taskKey === m.ScheduleTaskKey)?.parentName ?? m.Name,
  );
  return { members, labels };
}

/**
 * The per-test breakdown string for tooltips / expanded captions:
 * "15 min × 22 — Rock UCS · 15 min × 12 — Rock TCS". Parts without a parent
 * name fall back to the count alone.
 */
export function prepBreakdownLabel(group: PrepGroup): string {
  return group.parts
    .map((p) => {
      const rate = p.perTestMinutes != null ? `${p.perTestMinutes} min × ` : '× ';
      const qty = p.qty != null ? String(p.qty) : '?';
      return `${rate}${qty}${p.parentName ? ` — ${p.parentName}` : ''}`;
    })
    .join(' · ');
}

/** Compact totals cell text: "74 tests · 1,110 min" (either half optional). */
export function prepTotalsLabel(group: PrepGroup): string {
  const parts: string[] = [];
  if (group.totalTests != null) parts.push(`${group.totalTests} test${group.totalTests === 1 ? '' : 's'}`);
  if (group.totalMinutes != null) parts.push(`${Math.round(group.totalMinutes).toLocaleString('en-CA')} min`);
  return parts.join(' · ') || '—';
}
